import { __awaiter } from "tslib";
import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch } from '@/store/hooks';
import { clearTokenGetters, getToken } from '@/auth/auth-access';
import { clearTenantName, getTenantName } from '@/utils';
import { useFeatureFlagsProvider } from '@/search-for-retail/feature-flags';
import { getAuth0Audience } from '@/auth/utilities';
import * as ActionCreators from '@/store/actions/creators';
import { addError } from '@/store/slices/notificationsSlice';
import { resetRefAppState } from '@/search-for-retail/evaluate';
import { ls, USER_PROFILE_CACHE_KEY } from '@/stores/local-storage';
import { ss } from '@/stores/session-storage';
import { STORAGE_KEY } from '@/stores/stores.constants';
import { logError } from '@/utils/logger';
// Auth token
const useToken = () => {
    const [token, setToken] = useState('');
    useEffect(() => {
        const getAuth0AccessToken = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const accessToken = yield getToken();
                setToken(accessToken);
            }
            catch (e) {
                // do nothing
            }
        });
        void getAuth0AccessToken();
    }, []);
    return token;
};
export const useAuth0Token = () => {
    const { getAccessTokenSilently } = useAuth0();
    const tenantName = getTenantName();
    const getToken = useCallback((...args_1) => __awaiter(void 0, [...args_1], void 0, function* (cacheMode = 'on') {
        if (!tenantName) {
            return null;
        }
        // `tenantName` is stored on login form submit handler
        // tenant is persisted through auth0Provider `handleRedirectCallback` after returning to application
        // audience is constructed from `tenant` in login form
        const audience = getAuth0Audience(tenantName);
        try {
            return yield getAccessTokenSilently({ cacheMode, authorizationParams: { audience } });
        }
        catch (e) {
            return null;
        }
    }), [getAccessTokenSilently, tenantName]);
    return {
        getToken,
    };
};
// still used in:
// 1. app-layout.tsx - ENRICH_PROVIDER_VALUE
// 2. navigation.provider.tsx - to check if token is present
export const useAuthToken = useToken;
export const useGetToken = () => {
    const { getToken: getAuth0Token } = useAuth0Token();
    return useCallback(() => __awaiter(void 0, void 0, void 0, function* () { return (getAuth0Token()); }), [getAuth0Token]);
};
// Logout
export const useAuthLogout = () => {
    const { logout: auth0Logout } = useAuth0();
    const { featureFlagsService } = useFeatureFlagsProvider();
    const dispatch = useAppDispatch();
    const logout = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield auth0Logout({
            logoutParams: {
                returnTo: `${window.location.origin}`,
            },
            /* eslint-disable-next-line  @typescript-eslint/require-await */
            openUrl(url) {
                return __awaiter(this, void 0, void 0, function* () {
                    // const parsedUrl = new URL(url);
                    // The `openUrl` function instructs Auth0 how to handle redirects. This is particularly important because
                    // we have a custom routing library and navigations to same-origin URLs need to go through the routing library
                    // to keep the internal routing state consistent. `url` will contain the URL to redirect to in order to
                    // complete the logout process.
                    //
                    // Our `redirect` function doesn't work with cross-origin URLs. In these cases, we use the browser native
                    // `assign` function. This distinction ensures that our routing state remains consistent for same-origin URLs
                    // while still allowing for cross-origin redirects when necessary.
                    // if (parsedUrl.origin === window.location.origin) {
                    //   redirect(url);
                    // } else {
                    try {
                        dispatch(ActionCreators.logout());
                        resetRefAppState();
                        // clear local storage
                        clearTenantName();
                        // clear session storage
                        featureFlagsService.clear();
                        ss.clear(STORAGE_KEY);
                        ls.clear(USER_PROFILE_CACHE_KEY);
                        clearTokenGetters();
                        yield featureFlagsService.reset();
                    }
                    catch (error) {
                        const errorMessage = 'UNKNOWN_ERROR';
                        logError(errorMessage, error);
                        dispatch(addError({ key: errorMessage }));
                    }
                    window.location.assign(url);
                    // }
                });
            },
        });
    }), [auth0Logout, dispatch, featureFlagsService]);
    return { logout };
};
// Authenticated user email
const useUserEmailFromObject = (userObject) => {
    const [userEmail, setUserEmail] = useState('');
    useEffect(() => {
        if (userObject === null || userObject === void 0 ? void 0 : userObject.email) {
            setUserEmail(userObject.email);
        }
    }, [userObject]);
    return userEmail;
};
const useAuth0UserEmail = () => {
    const { user: auth0User } = useAuth0();
    return useUserEmailFromObject(auth0User);
};
export const useUserEmail = useAuth0UserEmail;
